import React, { createContext, useState, useContext, useEffect } from 'react'
import { AuthApi } from '../api/authApi'
import { logger } from '../utils/logger'
import jwt_decode, { JwtPayload } from 'jwt-decode'

const EXPIRY_OFFSET = 420
const SECONDS_MODIFIER = 1000

export interface AuthContext {
  readonly token: string
  readonly loading: boolean
  readonly error: Error | undefined
  readonly permissions: readonly string[]
  readonly login: () => void
  readonly logout: () => Promise<void>
  readonly refresh: () => Promise<void>
}

export interface AuthProviderProps {
  readonly authApi: AuthApi
  readonly children: React.ReactNode
}

interface JwtToken extends JwtPayload {
  readonly permissions: readonly string[]
}

const AuthContext = createContext<AuthContext | null>(null)

export function AuthProvider({
  authApi,
  children
}: AuthProviderProps): JSX.Element {
  const [token, setToken] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [permissions, setPermissions] = useState<readonly string[]>([])

  function resetTokenState(): void {
    setToken('')
    setError(undefined)
    setLoading(true)
    setPermissions([])
  }

  function setTokenState(token: string | undefined): void {
    if (!token) {
      logger.error('Failed to fetch token, auth provider has an error')

      setToken('')
      setPermissions([])
      setError(
        new Error(
          'Failed to to login due to incorrect password, username or server errors'
        )
      )
      setLoading(false)
      return
    }

    setToken(token)
    const decodedToken: JwtToken = jwt_decode(token)
    setPermissions(decodedToken.permissions)
    setError(undefined)
    setLoading(false)
  }

  async function refresh(): Promise<void> {
    logger.info('Attempting token refresh...')
    const token = await authApi.refresh()
    setTokenState(token)
  }

  function login(): void {
    logger.info('Attempting to login...')
    authApi.redirectToLoginPage()
  }

  async function logout(): Promise<void> {
    logger.info('Attempting to logout...')
    resetTokenState()
    await authApi.logout()
  }

  useEffect(() => {
    refresh()

    // Refresh the token every 7 minutes, remove the interval when this component leaves the screen
    const refreshInterval = setInterval(
      refresh,
      EXPIRY_OFFSET * SECONDS_MODIFIER
    )
    return (): void => clearInterval(refreshInterval)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        token,
        error,
        loading,
        refresh,
        login,
        logout,
        permissions
        //TODO: look into a rest ctx
      }}
      children={children}
    />
  )
}

export function useAuth(): AuthContext {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error(
      'Failed to use auth hook, are you sure you wrapped components with an AuthProvider?'
    )
  }

  return context
}
